import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};

export const ONBOARDING_STEPS = {
  KNOWLEDGE_BASE: {
    id: "knowledge-base",
    title: "Setup Knowledge Base",
    order: 1,
    icon: "📚",
  },
  UPLOAD_DOCUMENTS: {
    id: "upload-documents",
    title: "Upload Documents",
    order: 2,
    icon: "📄",
  },
  SCHEDULING: {
    id: "scheduling",
    title: "Enable Scheduling",
    order: 3,
    icon: "📅",
  },
  WIDGET_CODE: {
    id: "widget-code",
    title: "Get Widget Code",
    order: 4,
    icon: "🚀",
  },
};

export const OnboardingProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(
    ONBOARDING_STEPS.KNOWLEDGE_BASE.id
  );
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [onboardingData, setOnboardingData] = useState({});
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);

  // Load saved state
  useEffect(() => {
    const saved = localStorage.getItem("onboardingState");
    console.log("saved---->", saved);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCurrentStep(
          parsed.currentStep || ONBOARDING_STEPS.KNOWLEDGE_BASE.id
        );
        console.log("parsed data---->", parsed);
        setCompletedSteps(new Set(parsed.completedSteps || []));
        setOnboardingData(parsed.onboardingData || {});
        setIsOnboardingComplete(parsed.isOnboardingComplete || false);
      } catch (err) {
        console.error("Error loading onboarding state:", err);
      }
    }
  }, []);

  // Save state (only if changed)
  useEffect(() => {
    const stateToSave = {
      currentStep,
      completedSteps: Array.from(completedSteps),
      onboardingData,
      isOnboardingComplete,
    };

    console.log("stateToSave ---->", stateToSave);

    const saved = localStorage.getItem("onboardingState");
    if (saved !== JSON.stringify(stateToSave)) {
      localStorage.setItem("onboardingState", JSON.stringify(stateToSave));
    }
  }, [currentStep, completedSteps, onboardingData, isOnboardingComplete]);

  // Helper functions
  const getAllSteps = useCallback(
    () => Object.values(ONBOARDING_STEPS).sort((a, b) => a.order - b.order),
    []
  );
  const getCurrentStepObject = useCallback(
    () => Object.values(ONBOARDING_STEPS).find((s) => s.id === currentStep),
    [currentStep]
  );
  const getNextStep = useCallback(() => {
    const all = getAllSteps();
    const idx = all.findIndex((s) => s.id === currentStep);
    return idx < all.length - 1 ? all[idx + 1] : null;
  }, [currentStep, getAllSteps]);
  const getPreviousStep = useCallback(() => {
    const all = getAllSteps();
    const idx = all.findIndex((s) => s.id === currentStep);
    return idx > 0 ? all[idx - 1] : null;
  }, [currentStep, getAllSteps]);

  // const completeStep = useCallback((stepId, data = {}) => {
  //   setCompletedSteps((prev) => new Set([...prev, stepId]));
  //   setOnboardingData((prev) => ({ ...prev, [stepId]: data }));
  // }, []);

  const completeStep = useCallback((stepId, data = {}) => {
    setCompletedSteps((prev) => new Set([...prev, stepId]));

    setOnboardingData((prev) => ({
      ...prev,
      [stepId]: data,
      stepValues: {
        ...prev.stepValues,
        [stepId]: data,
      },
    }));
  }, []);

  const completeStepAndAdvance = useCallback(
    (stepId, data = {}) => {
      completeStep(stepId, data);
      const nextStep = getNextStep();
      if (nextStep) setCurrentStep(nextStep.id);
      else setIsOnboardingComplete(true);
    },
    [completeStep, getNextStep]
  );

  const goToStep = useCallback((stepId) => setCurrentStep(stepId), []);
  const goToNextStep = useCallback(() => {
    const next = getNextStep();
    if (next) setCurrentStep(next.id);
  }, [getNextStep]);
  const goToPreviousStep = useCallback(() => {
    const prev = getPreviousStep();
    if (prev) setCurrentStep(prev.id);
  }, [getPreviousStep]);

  const isStepCompleted = useCallback(
    (stepId) => completedSteps.has(stepId),
    [completedSteps]
  );
  const getProgressPercentage = useCallback(
    () => Math.round((completedSteps.size / getAllSteps().length) * 100),
    [completedSteps, getAllSteps]
  );

  const resetOnboarding = useCallback(() => {
    setCurrentStep(ONBOARDING_STEPS.KNOWLEDGE_BASE.id);
    setCompletedSteps(new Set());
    setOnboardingData({});
    setIsOnboardingComplete(false);
    localStorage.removeItem("onboardingState");
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        currentStep,
        completedSteps,
        onboardingData,
        isOnboardingComplete,
        completeStep,
        completeStepAndAdvance,
        goToStep,
        goToNextStep,
        goToPreviousStep,
        resetOnboarding,
        setOnboardingData,
        setCompletedSteps,
        getAllSteps,
        getCurrentStepObject,
        getNextStep,
        getPreviousStep,
        isStepCompleted,
        getProgressPercentage,
        ONBOARDING_STEPS,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
